import { ScheduleDateType as SDType } from 'data/models/ScheduleDateType';
import { SchedulePolicy, ScheduleRule as Rule, ScheduleRel as Rel } from './scheduleChangeEngine';
import { ScheduleDateEntity } from 'data/models/ScheduleDateEntity';

import {
    MINIMUM_RES_DURATION_MINS,
    DIFFERENT_PIERGROUP_TRANSIT_MINS,
    SAME_PIERGROUP_TRANSIT_MINS
} from './scheduleConstants';

export const commonScheduleRules : SchedulePolicy = pc => [
    ...ipArrivalBeforeDeparture(pc),
    ...preserveMovementSequence(pc),
    ...pjoStartMatchesIPDeparture(pc),
    ...pjoEndMatchesIPArrival(pc),
    ...portArrivalPointHasNoDuration(pc),
    ...portDeparturePointHasNoDuration(pc),
];

const 
    getPJO = (date : ScheduleDateEntity) => date.JobOrder()?.PilotJobOrder() ?? null,
    isPortArrival = (date : ScheduleDateEntity) => date.Type() === SDType.IPArrival && (date.ItineraryPoint()?.IsPortArrival() ?? false),
    isPortDeparture = (date : ScheduleDateEntity) => date.Type() === SDType.IPDeparture && (date.ItineraryPoint()?.IsPortDeparture() ?? false),
    ipArrivalBeforeDeparture : SchedulePolicy = pc =>
        pc.Itinerary().map(ip =>
            ip.ArrivalEntity() &&
            ip.DepartureEntity() &&
            new Rule(ip.ArrivalEntity()!, Rel.LEq, ip.DepartureEntity()!, ip.BerthReservation() ? MINIMUM_RES_DURATION_MINS * 60 * 1000 : 0)
        ),
    preserveMovementSequence : SchedulePolicy = pc =>
        pc.MovementSeries().map((ip, i, ips) =>
            (i > 0 || null) && // skips first ip
            ip.ArrivalEntity() &&
            ips[i - 1].DepartureEntity() &&
            new Rule(ips[i - 1].DepartureEntity()!, Rel.LEq, ip.ArrivalEntity()!,
                ip.Location()?.Berth() && ips[i - 1].Location()?.Berth() ?
                    // Both IPs at a berth, i.e. this is a shift. Do piergroups match?
                    ip.Location()!.Berth()!.PierGroupId() === ips[i - 1].Location()?.Berth()?.PierGroupId() ?
                        // Yes, piergroups match. Use same piergroup transit duration.
                        SAME_PIERGROUP_TRANSIT_MINS * 60 * 1000 :
                        // No, piergroups don't match. Use different piergroup transit duration.
                        // TODO: Is this too strict? We're saying shifts btwn piergroups must take at least this
                        // duration, and this rule isn't some default, it's a rule that our linear constraint
                        // solver will try to preserve at all times. Do we want zero here for max flexibility to
                        // the user? Also, remember that a "shift" can also mean the in (or out) movement from
                        // (or to) an anchorage and that some berth mgrs like to have their BerthTransitOffsets
                        // set to zero, which presumbably means they'd prefer to model the time from (or to) the
                        // anchorage as zero too.
                        DIFFERENT_PIERGROUP_TRANSIT_MINS * 60 * 1000 :
                    // At least one IP isn't at a berth, e.g. this is the initial movement in from the pilot station.
                    // Allow a min of zero, as we have some installations where many berth managers set all their
                    // BerthTransitOffsets to zero (they don't care about representing that initial transit time).
                    0
            )
        ),
    pjoStartMatchesIPDeparture : SchedulePolicy = pc =>
        pc.Schedule().filter(d => d.Type() === SDType.PJOStart).map(pjoStart => {
            const ipDeparture = getPJO(pjoStart)?.FromPoint()?.DepartureEntity();
            return ipDeparture && new Rule(ipDeparture, Rel.Eq, pjoStart);
        }),
    pjoEndMatchesIPArrival : SchedulePolicy = pc => 
        pc.Schedule().filter(d => d.Type() === SDType.PJOEnd).map(pjoEnd => {
            const ipArrival = getPJO(pjoEnd)?.ToPoint()?.ArrivalEntity();
            return ipArrival && new Rule(ipArrival, Rel.Eq, pjoEnd);
        }),
    portArrivalPointHasNoDuration : SchedulePolicy = pc =>
        pc.Schedule().filter(isPortArrival).map(arr => {
            const dep = arr.ItineraryPoint()!.DepartureEntity();
            return dep && new Rule(arr, Rel.Eq, dep);
        }),
    portDeparturePointHasNoDuration : SchedulePolicy = pc =>
        pc.Schedule().filter(isPortDeparture).map(dep => {
            const arr = dep.ItineraryPoint()!.ArrivalEntity();
            return arr && new Rule(arr, Rel.Eq, dep);
        });



// WEBPACK FOOTER //
// ./apps/common/services/commonScheduleRules.ts